import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import CloseIcon from "@mui/icons-material/Close";

import ModalBasicLayout from "../../ModalBasicLayout";
import LinearLoaderWithTime from "../../../Loaders/LinearLoaderWithTime";
import IconButton from "../../../Buttons/IconButton";
import Button from "../../../Buttons/Button";
import TitlePage from "../../../Texts/TitlePage";
import GalleryFolderBar from "../../../Gallery/GalleryFolderBar";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  BUTTON_CLOSE,
  MODAL_GALLERY_ARIA_LABEL,
  MODAL_META_AD_LIBRARY_LOADING_FETCHING_ADS,
  MODAL_META_AD_LIBRARY_LOADING_PREPARING_GALLERY,
  MODAL_META_AD_LIBRARY_LOADING_RESOLVING_COMPETITORS,
} from "../../../../i18n/keysTranslations";
import { IS_ADLE_DOMAIN } from "../../../../utils/constants";
import {
  COMPETITORS_LOADING_TIME_MS,
  MODAL_COMPETITORS_BODY_SX,
  MODAL_COMPETITORS_FOOTER_LOADER_SX,
  MODAL_COMPETITORS_FOLDER_TOOLBAR_ROW_SX,
  MODAL_COMPETITORS_HEADER_ROW_SX,
  MODAL_COMPETITORS_SCROLL_AREA_SX,
  MODAL_COMPETITORS_TITLE_SX,
  MODAL_COMPETITORS_TOOLBAR_ROW_SX,
} from "../constants";
import CompetitorsFilterBar from "./CompetitorsFilterBar";
import CompetitorsGallery from "./CompetitorsGallery";
import CompetitorsInitialLoader from "./CompetitorsInitialLoader";
import CompetitorsScrapeProgressBar from "./CompetitorsScrapeProgressBar";
import CompetitorFavoriteToast from "./CompetitorFavoriteToast";
import FeatureAddonPaywallDialog from "../../../FeatureAddons/FeatureAddonPaywallDialog";
import { FEATURE_ADDON_KEYS } from "../../../../actions/featureAddons";

const NOOP = () => {};
const EMPTY_OBJECT = {};

const ModalCompetitorsLayout = ({
  modalOpen,
  onCloseModal = NOOP,
  title,
  isLoading = false,
  isLoadingContentReady = false,
  loadingDurationMs,
  onLoadingAnimationComplete = NOOP,
  error = null,
  totalTopOffsetPx = 0,
  emptyContentKey,
  favoriteToastProps = null,
  competitorsPaywall = null,
  emptyContentKeyOverride = null,
  showCompetitorFilter = false,
  showFolderBar = false,
  isUploading = false,
  isScrapingNewCompetitors = false,
  scrapingDurationMs = COMPETITORS_LOADING_TIME_MS,
  showUploadingFooter = false,
  uploadingFooterLabels = [],
  filtersProps = EMPTY_OBJECT,
  folderBarProps = EMPTY_OBJECT,
  galleryProps = EMPTY_OBJECT,
}) => {
  const { t } = useTranslationApp();
  const galleryItems = galleryProps.items || [];
  const hasGalleryItems = galleryItems.length > 0;
  const loadingLabels = [
    t(MODAL_META_AD_LIBRARY_LOADING_RESOLVING_COMPETITORS),
    t(MODAL_META_AD_LIBRARY_LOADING_FETCHING_ADS),
    t(MODAL_META_AD_LIBRARY_LOADING_PREPARING_GALLERY),
  ];

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      hasTitle={false}
      showCloseButton={false}
      fullWidth={true}
      fullScreen
      scroll="paper"
      aria-label={t(MODAL_GALLERY_ARIA_LABEL)}
      sxPaper={{
        display: "flex",
        flexDirection: "column",
      }}
      sxContent={{
        px: 0,
        pt: 0,
        pb: 0,
        mt: 0,
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={MODAL_COMPETITORS_HEADER_ROW_SX}>
        <TitlePage sx={MODAL_COMPETITORS_TITLE_SX}>{title}</TitlePage>
        <IconButton onClick={onCloseModal} tooltipText={BUTTON_CLOSE}>
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>

      {isLoading ? (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <CompetitorsInitialLoader
            durationMs={loadingDurationMs}
            isContentReady={isLoadingContentReady}
            onComplete={onLoadingAnimationComplete}
            labels={loadingLabels}
            labelSide={IS_ADLE_DOMAIN ? "bottom" : "top"}
            totalTopOffsetPx={totalTopOffsetPx}
          />
        </Box>
      ) : (
        <Box sx={MODAL_COMPETITORS_BODY_SX}>
          {showCompetitorFilter ? (
            <Box sx={MODAL_COMPETITORS_TOOLBAR_ROW_SX}>
              <CompetitorsFilterBar {...filtersProps} />
            </Box>
          ) : null}

          {showFolderBar ? (
            <Box sx={MODAL_COMPETITORS_FOLDER_TOOLBAR_ROW_SX}>
              <GalleryFolderBar canEdit {...folderBarProps} />
              {folderBarProps.showMoveFolderButton ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  <Button
                    color="secondary"
                    onClick={folderBarProps.onOpenMoveFolderModal}
                  >
                    {folderBarProps.moveFolderLabel}
                  </Button>
                </Box>
              ) : null}
            </Box>
          ) : null}

          <Box sx={MODAL_COMPETITORS_SCROLL_AREA_SX}>
            {error ? (
              <Box
                sx={{
                  minHeight: 280,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 3,
                }}
              >
                <Typography variant="body1" align="center" color="error.main">
                  {error}
                </Typography>
              </Box>
            ) : null}

            {!error && hasGalleryItems ? (
              <CompetitorsGallery {...galleryProps} />
            ) : null}

            {!error && !hasGalleryItems ? (
              <Box
                sx={{
                  minHeight: 280,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 3,
                }}
              >
                <Typography variant="h4" align="center">
                  {t(emptyContentKeyOverride || emptyContentKey)}
                </Typography>
              </Box>
            ) : null}
          </Box>

          {!error && isScrapingNewCompetitors ? (
            <CompetitorsScrapeProgressBar
              labels={loadingLabels}
              durationMs={scrapingDurationMs}
            />
          ) : null}

          {showUploadingFooter && isUploading ? (
            <DialogActions sx={MODAL_COMPETITORS_FOOTER_LOADER_SX}>
              <LinearLoaderWithTime
                labels={uploadingFooterLabels.map((labelKey) => t(labelKey))}
                time={30 * 1000}
                sx={{ width: "100%", maxWidth: "none" }}
              />
            </DialogActions>
          ) : null}
        </Box>
      )}

      {favoriteToastProps ? (
        <CompetitorFavoriteToast {...favoriteToastProps} />
      ) : null}
      {competitorsPaywall ? (
        <FeatureAddonPaywallDialog
          open={competitorsPaywall.open}
          onClose={competitorsPaywall.closePaywall}
          status={competitorsPaywall.status}
          addonKey={FEATURE_ADDON_KEYS.COMPETITORS}
          isLoading={competitorsPaywall.isLoading}
          isCheckingOut={competitorsPaywall.isCheckingOut}
          onCheckoutTier={competitorsPaywall.checkoutTier}
        />
      ) : null}
    </ModalBasicLayout>
  );
};

export default ModalCompetitorsLayout;
